
import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Select } from 'ui/select';

import type { SpecialAwardLogicCategory } from '../hooks/use-special-award-form';

interface DimensionOption {
  value: string;
  label: string;
}

interface ValueOption {
  value: string | number;
  label: string;
}

export interface SpecialAwardStudentDataSourceProps {
  blockId: string;
  category: string;
  optionIds: Array<string | number>;
  categories: SpecialAwardLogicCategory[];
  onCategoryChange: (category: string) => void;
  onOptionsChange: (optionIds: Array<string | number>) => void;
  isLoading?: boolean;
}

/** The Dimension/Value(s) pair for one Student-data criteria block. */
const SpecialAwardStudentDataSource = ({
  blockId,
  category,
  optionIds,
  categories,
  onCategoryChange,
  onOptionsChange,
  isLoading = false,
}: SpecialAwardStudentDataSourceProps) => {
  const selectedCategory = categories.find(
    (item) => item.category === category
  );
  const dimensionOptions = categories.map<DimensionOption>((item) => ({
    value: item.category,
    label: item.category,
  }));
  const valueOptions = (selectedCategory?.options ?? []).map<ValueOption>(
    (option) => ({ value: option.id, label: option.name })
  );
  const selectedDimension =
    dimensionOptions.find((option) => option.value === category) ?? null;
  const selectedValues = valueOptions.filter((option) =>
    optionIds.some((id) => String(id) === String(option.value))
  );

  return (
    <Stack
      direction="row"
      sx={{ width: '100%', alignItems: 'flex-end', gap: 1.5, pr: '54px' }}
      data-testid="special-award-student-data-source"
      data-modal-keyboard-ignore
    >
      <Box sx={{ width: '10rem' }}>
        <Select<DimensionOption>
          label="Dimension"
          required
          loading={isLoading}
          options={dimensionOptions}
          value={selectedDimension ?? undefined}
          onChange={(option) => {
            if (option && !Array.isArray(option)) {
              onCategoryChange(option.value);
            }
          }}
          placeholder="Select..."
          data-testid={`student-data-dimension-${blockId}`}
        />
      </Box>

      {selectedDimension && (
        <Box sx={{ minWidth: 0, flex: '1 1 0%' }}>
          <Select<ValueOption>
            label="Value(s)"
            required
            multiple
            options={valueOptions}
            value={selectedValues}
            onChange={(value) =>
              onOptionsChange(
                (Array.isArray(value) ? value : [value])
                  .filter((option): option is ValueOption => !!option)
                  .map((option) => option.value)
              )
            }
            clearable
            placeholder="Select..."
            data-testid={`student-data-values-${blockId}`}
          />
        </Box>
      )}
    </Stack>
  );
};

export default SpecialAwardStudentDataSource;
